import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Sparkles, ArrowRight, ArrowLeft } from "lucide-react";

const DIMENSIONS = [
  {
    key: "entity_recognition",
    label: "Entity Recognition",
    promptRef: "A",
    criteria: [
      ["0–5",   "AI confused, incorrect, or no data"],
      ["6–12",  "Basic description only, vague"],
      ["13–20", "Clear, confident, accurate description"],
    ],
  },
  {
    key: "category_inclusion",
    label: "AI Discovery Presence",
    promptRef: "B",
    criteria: [
      ["0",     "Absent from category-level recommendations"],
      ["10",    "Listed inconsistently across platforms"],
      ["20",    "Consistently recommended across all platforms"],
    ],
  },
  {
    key: "competitive_association",
    label: "Competitive Association",
    promptRef: "C",
    criteria: [
      ["0",     "Not grouped with any competitors"],
      ["10",    "Weak or partial association"],
      ["20",    "Clearly grouped and compared"],
    ],
  },
  {
    key: "accuracy_consistency",
    label: "Accuracy & Consistency",
    promptRef: "all",
    criteria: [
      ["0–5",   "Contradictory or inconsistent"],
      ["6–12",  "Mostly aligned with minor gaps"],
      ["13–20", "Highly consistent and accurate"],
    ],
  },
  {
    key: "authority_signals",
    label: "Authority & Confidence",
    promptRef: "all",
    criteria: [
      ["0–5",   "Weak, hedging, or no signals"],
      ["6–12",  "Moderate authority language"],
      ["13–20", "Strong confidence and authority"],
    ],
  },
];

const PLATFORM_LABEL = {
  openai: "ChatGPT", anthropic: "Claude", gemini: "Gemini", perplexity: "Perplexity",
};

function ScoreSlider({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-4">
      <input
        type="range"
        min="0" max="20" step="1"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        data-testid={testId}
        className="flex-1 accent-[#b8963e] cursor-pointer"
        style={{ height: 6 }}
      />
      <span className="font-serif-display font-bold text-3xl text-[#f4efe6] w-14 text-right">
        {value}<span className="text-xs text-[#5e5448] font-mono-eav">/20</span>
      </span>
    </div>
  );
}

export default function ManualScore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});
  const [suggested, setSuggested] = useState({});
  const [reasoning, setReasoning] = useState({});
  const [suggesting, setSuggesting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/audits/${id}/prompts`);
        setData(data);
        const initialScores = {};
        const initialNotes = {};
        DIMENSIONS.forEach((d) => {
          initialScores[d.key] = data.manual_scores?.[d.key]
            ?? data.suggested_scores?.[d.key]
            ?? 10;
          initialNotes[d.key] = data.manual_score_notes?.[d.key] || "";
        });
        setScores(initialScores);
        setNotes(initialNotes);
        setSuggested(data.suggested_scores || {});
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [id]);

  const requestSuggestions = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/audits/${id}/suggest`);
      setSuggested(data.suggested_scores || {});
      setReasoning(data.score_reasoning || {});
      // Snap sliders to suggestions if user hasn't moved them yet (rough heuristic)
      const next = { ...scores };
      DIMENSIONS.forEach((d) => {
        next[d.key] = data.suggested_scores?.[d.key] ?? scores[d.key];
      });
      setScores(next);
    } catch (e) {
      alert(formatApiError(e));
    }
    setSuggesting(false);
  };

  const applySuggestion = (key) => {
    if (suggested[key] != null) setScores((p) => ({ ...p, [key]: suggested[key] }));
  };

  const saveScores = async () => {
    await api.put(`/audits/${id}/manual-scores`, { scores, notes });
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      await saveScores();
      await api.post(`/audits/${id}/finalize`);
      navigate(`/snapshot/${id}`);
    } catch (e) {
      alert(formatApiError(e));
      setFinalizing(false);
    }
  };

  if (err) return <div className="max-w-3xl mx-auto p-12 text-[#c46a5a]" data-testid="manualscore-error">{err}</div>;
  if (!data) return <div className="max-w-3xl mx-auto p-12 text-[#c9bca4] font-mono-eav text-sm" data-testid="manualscore-loading">Loading…</div>;

  const total = Object.values(scores).reduce((a, b) => a + (b || 0), 0);
  const tier =
    total <= 30 ? { label: "Invisible",         color: "#c46a5a" } :
    total <= 50 ? { label: "Underrepresented", color: "#d4a04a" } :
    total <= 70 ? { label: "Emerging",          color: "#b8963e" } :
                  { label: "Recommended",       color: "#6db66d" };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="manualscore-page">
      <div className="flex flex-wrap justify-between gap-3 mb-6">
        <Link to={`/audit/${id}/manual`} data-testid="manualscore-back"
              className="text-sm text-[#8a7e6d] hover:text-[#b8963e] transition-colors font-mono-eav tracking-wider inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> BACK TO PROMPTS
        </Link>
      </div>

      <p className="overline">Manual Run · Step 2 of 2</p>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-[1.05]" style={{fontWeight: 700}}>
        Score the <em className="text-[#b8963e] italic font-normal">five dimensions.</em>
      </h1>
      <p className="mt-3 text-[#c9bca4] max-w-2xl">
        {`Review your pasted outputs and score each dimension 0–20. Click "Get AI-suggested scores" to have Claude analyze your pastes and pre-fill the sliders for you.`}
      </p>

      {/* Top bar — running total + suggest button */}
      <div className="mt-8 eav-card p-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="overline-soft">Running Total</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-serif-display font-bold text-5xl text-[#f4efe6]" data-testid="manualscore-total">{total}</span>
            <span className="text-[#5e5448] font-mono-eav text-sm">/ 100</span>
            <span className="ml-3 font-mono-eav text-xs tracking-wider uppercase" style={{ color: tier.color }} data-testid="manualscore-tier">
              ● {tier.label}
            </span>
          </div>
        </div>
        <button onClick={requestSuggestions} disabled={suggesting}
                data-testid="manualscore-suggest"
                className="btn-ghost text-sm py-2">
          <Sparkles className="w-4 h-4" />
          {suggesting ? "Analyzing pastes…" : "Get AI-suggested scores"}
        </button>
      </div>

      {/* Dimensions */}
      <div className="mt-8 space-y-5">
        {DIMENSIONS.map((dim) => {
          const userScore = scores[dim.key] ?? 0;
          const sug = suggested[dim.key];
          const reason = reasoning[dim.key];
          // Relevant outputs for this dimension
          const platforms = ["openai", "gemini", "anthropic", "perplexity"];
          const showOutputs = dim.promptRef === "all"
            ? platforms.flatMap((p) => ["A", "B", "C"].map((pid) => ({ p, pid })))
            : platforms.map((p) => ({ p, pid: dim.promptRef }));
          return (
            <section key={dim.key} data-testid={`dimension-${dim.key}`} className="eav-card overflow-hidden">
              <div className="px-6 py-5 border-b border-[#2a2620]" style={{ background: "#0a0908" }}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <p className="overline">{dim.label}</p>
                    <p className="mt-1 font-mono-eav text-[10px] tracking-wider text-[#5e5448]">
                      {dim.promptRef === "all" ? "ANALYZES ALL PROMPTS" : `BASED ON PROMPT ${dim.promptRef}`}
                    </p>
                  </div>
                  {sug != null && (
                    <button onClick={() => applySuggestion(dim.key)}
                            data-testid={`use-suggestion-${dim.key}`}
                            className="font-mono-eav text-[11px] tracking-wider uppercase px-3 py-1.5 rounded-sm border border-[#b8963e] bg-[rgba(184,150,62,0.12)] text-[#b8963e] hover:bg-[rgba(184,150,62,0.2)] transition-colors">
                      <Sparkles className="w-3 h-3 inline mr-1" /> Suggested: {sug}/20 — Use
                    </button>
                  )}
                </div>
                {/* Criteria */}
                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                  {dim.criteria.map(([range, desc]) => (
                    <div key={range} className="text-xs">
                      <span className="font-mono-eav font-bold text-[#b8963e] mr-2">{range}</span>
                      <span className="text-[#c9bca4]">{desc}</span>
                    </div>
                  ))}
                </div>
                {reason && (
                  <p className="mt-4 text-sm italic font-serif-display text-[#c9bca4] border-l-2 border-[#b8963e]/40 pl-3">
                    {reason}
                  </p>
                )}
              </div>

              <div className="px-6 py-5">
                {/* Slider */}
                <ScoreSlider
                  value={userScore}
                  onChange={(v) => setScores((p) => ({ ...p, [dim.key]: v }))}
                  testId={`slider-${dim.key}`}
                />

                {/* Notes */}
                <label className="overline-soft mt-5 block">Notes</label>
                <textarea
                  rows={2}
                  data-testid={`notes-${dim.key}`}
                  value={notes[dim.key] || ""}
                  onChange={(e) => setNotes((p) => ({ ...p, [dim.key]: e.target.value }))}
                  placeholder="Why this score?"
                  className="mt-2 eav-input"
                />

                {/* Relevant outputs preview */}
                <details className="mt-4 group">
                  <summary className="cursor-pointer list-none font-mono-eav text-[11px] tracking-wider text-[#8a7e6d] hover:text-[#b8963e] transition-colors flex items-center gap-1">
                    <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                    SHOW PASTED OUTPUTS USED FOR THIS DIMENSION
                  </summary>
                  <div className="mt-3 space-y-3">
                    {showOutputs.map(({ p, pid }, i) => {
                      const text = ((data.manual_outputs?.[p] || {})[pid] || "").trim();
                      if (!text) return null;
                      return (
                        <div key={i} className="border border-[#2a2620] rounded-sm p-3 bg-[#0a0908]">
                          <p className="font-mono-eav text-[10px] tracking-wider text-[#b8963e] mb-2">
                            {PLATFORM_LABEL[p]} · PROMPT {pid}
                          </p>
                          <pre className="whitespace-pre-wrap font-mono-eav text-xs text-[#c9bca4] leading-relaxed max-h-40 overflow-y-auto">
                            {text}
                          </pre>
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
        <button onClick={saveScores} data-testid="manualscore-save"
                className="btn-ghost text-sm py-2">
          Save draft
        </button>
        <button onClick={finalize} disabled={finalizing}
                data-testid="manualscore-finalize"
                className="btn-gold">
          {finalizing ? "Generating report…" : "Generate Report"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
